import React, { useEffect, useId, useMemo, useRef } from 'react';
import {
  LuArrowDown,
  LuArrowUp,
  LuArrowUpDown,
  LuCheck,
  LuChevronRight,
  LuClock,
  LuExternalLink,
  LuInbox,
  LuInfo,
  LuLoader,
  LuMinus,
  LuRefreshCw,
  LuTriangleAlert,
  LuX,
} from 'react-icons/lu';
import { createPortal } from 'react-dom';
import {
  formatDateTime,
  formatDuration,
  formatPercent,
  normalizeStatus,
  passRateTone,
  PATTERN_META,
} from './reportData';
import { Pagination } from '../ui';

export function ReportSection({ title, subtitle, actions, children, className = '', id, flush = false }) {
  const headingId = id ? `${id}-title` : undefined;
  return (
    <section className={`rpt-section ${flush ? 'rpt-section--flush' : ''} ${className}`.trim()} aria-labelledby={headingId} id={id}>
      {(title || actions) && (
        <header className="rpt-section__head">
          <div className="rpt-section__titles">
            {title && <h2 className="rpt-section__title" id={headingId}>{title}</h2>}
            {subtitle && <p className="rpt-section__subtitle">{subtitle}</p>}
          </div>
          {actions && <div className="rpt-section__actions">{actions}</div>}
        </header>
      )}
      <div className="rpt-section__body">{children}</div>
    </section>
  );
}

export function ReportKpi({ label, value, hint, delta, deltaGood, tone, loading }) {
  return (
    <div className={`rpt-kpi ${tone ? `rpt-kpi--${tone}` : ''}`}>
      <span className="rpt-kpi__label">{label}</span>
      {loading ? (
        <span className="rt-skeleton rpt-kpi__skel" aria-hidden />
      ) : (
        <span className="rpt-kpi__value">{value}</span>
      )}
      {!loading && (delta || hint) && (
        <span className="rpt-kpi__meta">
          {delta && (
            <span
              className={`rpt-delta ${
                deltaGood == null ? '' : deltaGood ? 'rpt-delta--good' : 'rpt-delta--bad'
              }`}
            >
              {delta}
            </span>
          )}
          {hint && <span className="rpt-kpi__hint">{hint}</span>}
        </span>
      )}
    </div>
  );
}

export const KpiStrip = ({ children, label }) => (
  <div className="rpt-kpi-strip" role="group" aria-label={label}>
    {children}
  </div>
);

const STATUS_META = {
  Passed: { tone: 'success', Icon: LuCheck },
  Failed: { tone: 'danger', Icon: LuX },
  Pending: { tone: 'warning', Icon: LuClock },
  Skipped: { tone: 'neutral', Icon: LuMinus },
  Running: { tone: 'info', Icon: LuLoader },
};

export function StatusPill({ status }) {
  const label = normalizeStatus(status);
  const meta = STATUS_META[label] || { tone: 'neutral', Icon: null };
  const { Icon } = meta;
  return (
    <span className={`rpt-pill rpt-pill--${meta.tone}`}>
      {Icon && <Icon size={11} strokeWidth={2.75} aria-hidden />}
      {label}
    </span>
  );
}

export function PatternBadge({ pattern }) {
  const meta = PATTERN_META[pattern];
  if (!meta || pattern === 'stable') return <span className="rpt-muted">—</span>;
  return (
    <span className={`rpt-tag rpt-tag--${meta.tone}`} title={meta.hint}>
      {meta.label}
    </span>
  );
}

export function HealthPill({ rate, label }) {
  const tone = passRateTone(rate);
  const text =
    label ||
    { success: 'Healthy', warning: 'At risk', danger: 'Critical', neutral: 'No data' }[tone];
  return (
    <span className={`rpt-health rpt-health--${tone}`}>
      <span className="rpt-health__dot" aria-hidden />
      {text}
    </span>
  );
}

export function PassRateBar({ rate, compact = false }) {
  if (rate == null || rate === '' || Number.isNaN(Number(rate))) {
    return <span className="rpt-muted">—</span>;
  }
  const value = Math.max(0, Math.min(100, Number(rate)));
  const tone = passRateTone(value);
  return (
    <span className={`rpt-rate ${compact ? 'rpt-rate--compact' : ''}`} title={`${value}% pass rate`}>
      <span className="rpt-rate__track" aria-hidden>
        <span className={`rpt-rate__fill rpt-fill--${tone}`} style={{ width: `${value}%` }} />
      </span>
      <span className={`rpt-rate__value rpt-text--${tone}`}>{formatPercent(value, value % 1 ? 1 : 0)}</span>
    </span>
  );
}

/**
 * Single 100% stacked bar describing how executions resolved.
 * segments: [{ key, label, value, tone }]
 */
export function OutcomeBar({ segments, total: totalProp, caption }) {
  const total = totalProp ?? segments.reduce((a, s) => a + (Number(s.value) || 0), 0);
  const visible = segments.filter((s) => Number(s.value) > 0);
  const summary = segments.map((s) => `${s.label} ${s.value}`).join(', ');
  return (
    <div className="rpt-outcome">
      <div className="rpt-outcome__bar" role="img" aria-label={`Execution outcome: ${summary}`}>
        {total > 0 ? (
          visible.map((s) => (
            <span
              key={s.key}
              className={`rpt-outcome__seg rpt-fill--${s.tone}`}
              style={{ flexGrow: Number(s.value) }}
              title={`${s.label}: ${s.value} (${((s.value / total) * 100).toFixed(1)}%)`}
            />
          ))
        ) : (
          <span className="rpt-outcome__seg rpt-outcome__seg--empty" />
        )}
      </div>
      <ul className="rpt-outcome__legend">
        {segments.map((s) => (
          <li key={s.key}>
            <span className={`rpt-swatch rpt-fill--${s.tone}`} aria-hidden />
            <span className="rpt-outcome__label">{s.label}</span>
            <span className="rpt-outcome__value">{Number(s.value).toLocaleString()}</span>
            <span className="rpt-outcome__pct">
              {total > 0 ? `${((s.value / total) * 100).toFixed(s.value / total < 0.1 && s.value > 0 ? 1 : 0)}%` : '—'}
            </span>
          </li>
        ))}
      </ul>
      {caption && <p className="rpt-caption">{caption}</p>}
    </div>
  );
}

/**
 * Horizontal bar list — long labels stay readable.
 * items: [{ key, label, value, display?, tone?, sub? , onClick? }]
 */
export function HBarList({ items, max: maxProp, emptyText = 'No data for this period.', tone = 'danger', ariaLabel }) {
  if (!items.length) return <p className="rpt-empty-inline">{emptyText}</p>;
  const max = maxProp ?? Math.max(1, ...items.map((i) => Number(i.value) || 0));
  return (
    <ul className="rpt-hbars" aria-label={ariaLabel}>
      {items.map((item) => {
        const width = Math.max(2, ((Number(item.value) || 0) / max) * 100);
        const content = (
          <>
            <span className="rpt-hbars__label" title={item.label}>
              {item.label}
              {item.sub && <span className="rpt-hbars__sub">{item.sub}</span>}
            </span>
            <span className="rpt-hbars__track" aria-hidden>
              <span className={`rpt-hbars__fill rpt-fill--${item.tone || tone}`} style={{ width: `${width}%` }} />
            </span>
            <span className="rpt-hbars__value">{item.display ?? item.value}</span>
          </>
        );
        return (
          <li key={item.key}>
            {item.onClick ? (
              <button type="button" className="rpt-hbars__row rpt-hbars__row--btn" onClick={item.onClick}>
                {content}
              </button>
            ) : (
              <div className="rpt-hbars__row">{content}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export function ReportEmptyState({ icon, title, description, action, compact = false }) {
  return (
    <div className={`rpt-state ${compact ? 'rpt-state--compact' : ''}`} role="status">
      <span className="rpt-state__icon" aria-hidden>{icon || <LuInbox size={20} />}</span>
      <h3 className="rpt-state__title">{title}</h3>
      {description && <p className="rpt-state__desc">{description}</p>}
      {action}
    </div>
  );
}

export function ReportErrorState({
  title = 'Unable to load report',
  description = "We couldn't retrieve the report data. Your filters are kept.",
  onRetry,
  compact = false,
}) {
  return (
    <div className={`rpt-state rpt-state--error ${compact ? 'rpt-state--compact' : ''}`} role="alert">
      <span className="rpt-state__icon" aria-hidden><LuTriangleAlert size={20} /></span>
      <h3 className="rpt-state__title">{title}</h3>
      {description && <p className="rpt-state__desc">{description}</p>}
      {onRetry && (
        <button type="button" className="rt-btn rt-btn--secondary rt-btn--sm" onClick={onRetry}>
          <LuRefreshCw size={14} aria-hidden />
          <span>Try again</span>
        </button>
      )}
    </div>
  );
}

export function ReportSkeleton({ variant = 'block', rows = 4, height }) {
  if (variant === 'kpis') {
    return (
      <div className="rpt-kpi-strip" aria-hidden>
        {Array.from({ length: rows }).map((_, i) => (
          <div className="rpt-kpi" key={i}>
            <span className="rt-skeleton" style={{ width: '50%', height: 10 }} />
            <span className="rt-skeleton rpt-kpi__skel" />
          </div>
        ))}
      </div>
    );
  }
  if (variant === 'list') {
    return (
      <div className="rpt-skel-list" aria-hidden>
        {Array.from({ length: rows }).map((_, i) => (
          <div className="rpt-skel-list__row" key={i}>
            <span className="rt-skeleton" style={{ width: `${40 + ((i * 13) % 35)}%` }} />
            <span className="rt-skeleton" style={{ width: 48 }} />
          </div>
        ))}
      </div>
    );
  }
  return <div className="rt-skeleton rpt-skel-block" style={{ height: height || 180 }} aria-hidden />;
}

export const InlineNote = ({ children }) => <p className="rpt-note">{children}</p>;

export const parseSort = (value) => {
  const [key, dir] = String(value || '').split(':');
  return key ? { key, dir: dir === 'asc' ? 'asc' : 'desc' } : null;
};

const compare = (a, b) => {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
};

/**
 * columns: [{ key, header, render?(row), sortValue?(row), sortable?, align?, width?, className? }]
 * Sorting is applied to the rows currently loaded (the visible page).
 */
export function ReportTable({
  columns,
  rows = [],
  rowKey = 'id',
  loading = false,
  fetching = false,
  error = null,
  onRetry,
  onRowClick,
  rowLabel,
  rowClassName,
  sort,
  onSortChange,
  pagination,
  emptyTitle = 'No results',
  emptyDescription,
  caption,
  skeletonRows = 6,
  maxHeight,
}) {
  const sortState = parseSort(sort);

  const sortedRows = useMemo(() => {
    if (!sortState) return rows;
    const col = columns.find((c) => c.key === sortState.key);
    if (!col) return rows;
    const getter = col.sortValue || ((r) => r[col.key]);
    const out = [...rows].sort((a, b) => compare(getter(a), getter(b)));
    return sortState.dir === 'desc' ? out.reverse() : out;
  }, [rows, columns, sortState]);

  const toggleSort = (key) => {
    if (!onSortChange) return;
    if (!sortState || sortState.key !== key) onSortChange(`${key}:desc`);
    else if (sortState.dir === 'desc') onSortChange(`${key}:asc`);
    else onSortChange('');
  };

  const keyFor = (row, idx) => (typeof rowKey === 'function' ? rowKey(row, idx) : row?.[rowKey] ?? idx);

  if (error && !rows.length) {
    return <ReportErrorState compact onRetry={onRetry} />;
  }

  const showSkeleton = loading && !rows.length;

  return (
    <div className="rpt-table-block">
      <div
        className={`rpt-table-wrap ${fetching && rows.length ? 'is-fetching' : ''}`}
        style={maxHeight ? { maxHeight } : undefined}
        aria-busy={loading || fetching || undefined}
      >
        <table className="rpt-table">
          {caption && <caption className="rpt-sr-only">{caption}</caption>}
          <thead>
            <tr>
              {columns.map((col) => {
                const sortable = col.sortable && onSortChange;
                const active = sortState?.key === col.key;
                const ariaSort = active ? (sortState.dir === 'asc' ? 'ascending' : 'descending') : undefined;
                return (
                  <th
                    key={col.key}
                    scope="col"
                    className={`${col.align ? `is-${col.align}` : ''} ${col.className || ''}`.trim() || undefined}
                    style={col.width ? { width: col.width } : undefined}
                    aria-sort={sortable ? ariaSort || 'none' : undefined}
                  >
                    {sortable ? (
                      <button
                        type="button"
                        className={`rpt-th-sort ${active ? 'is-active' : ''}`}
                        onClick={() => toggleSort(col.key)}
                        title="Sort rows on this page"
                      >
                        {col.header}
                        {active ? (
                          sortState.dir === 'asc' ? <LuArrowUp size={12} aria-hidden /> : <LuArrowDown size={12} aria-hidden />
                        ) : (
                          <LuArrowUpDown size={12} aria-hidden className="rpt-th-sort__idle" />
                        )}
                      </button>
                    ) : (
                      col.header
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {showSkeleton &&
              Array.from({ length: skeletonRows }).map((_, i) => (
                <tr key={`sk-${i}`} className="rpt-table__skeleton">
                  {columns.map((col, j) => (
                    <td key={col.key}>
                      <span className="rt-skeleton" style={{ width: `${45 + ((i * 7 + j * 11) % 40)}%` }} />
                    </td>
                  ))}
                </tr>
              ))}
            {!showSkeleton &&
              sortedRows.map((row, idx) => {
                const clickable = typeof onRowClick === 'function';
                return (
                  <tr
                    key={keyFor(row, idx)}
                    className={`${clickable ? 'is-clickable' : ''} ${rowClassName ? rowClassName(row) || '' : ''}`.trim() || undefined}
                    onClick={clickable ? () => onRowClick(row) : undefined}
                    onKeyDown={
                      clickable
                        ? (e) => {
                            if (e.target !== e.currentTarget) return;
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              onRowClick(row);
                            }
                          }
                        : undefined
                    }
                    tabIndex={clickable ? 0 : undefined}
                    aria-label={clickable && rowLabel ? rowLabel(row) : undefined}
                  >
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        className={`${col.align ? `is-${col.align}` : ''} ${col.className || ''}`.trim() || undefined}
                      >
                        {col.render ? col.render(row) : row[col.key] ?? '—'}
                      </td>
                    ))}
                  </tr>
                );
              })}
          </tbody>
        </table>
        {!showSkeleton && !rows.length && (
          <ReportEmptyState compact title={emptyTitle} description={emptyDescription} />
        )}
      </div>
      {pagination && pagination.total > 0 && (
        <Pagination
          className="rpt-pagination"
          page={pagination.page}
          pageSize={pagination.pageSize}
          total={pagination.total}
          onPageChange={pagination.onPageChange}
          onPageSizeChange={pagination.onPageSizeChange}
          pageSizeOptions={pagination.pageSizeOptions || [10, 20, 50]}
          loading={fetching}
        />
      )}
    </div>
  );
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function ReportDrawer({ open, onClose, title, eyebrow, badge, children, footer }) {
  const panelRef = useRef(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const previouslyFocused = document.activeElement;
    const raf = requestAnimationFrame(() => {
      panelRef.current?.querySelector(FOCUSABLE)?.focus({ preventScroll: true });
    });

    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose?.();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const nodes = Array.from(panelRef.current.querySelectorAll(FOCUSABLE));
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey);
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="rpt-drawer-overlay" onMouseDown={onClose} role="presentation" data-rt-modal-overlay>
      <aside
        ref={panelRef}
        className="rpt-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="rpt-drawer__head">
          <div className="rpt-drawer__titles">
            {eyebrow && <span className="rpt-drawer__eyebrow">{eyebrow}</span>}
            <h2 className="rpt-drawer__title" id={titleId}>{title}</h2>
            {badge && <div className="rpt-drawer__badges">{badge}</div>}
          </div>
          <button type="button" className="rt-btn rt-btn--tertiary rt-btn--icon rt-btn--sm" onClick={onClose} aria-label="Close details">
            <LuX size={16} aria-hidden />
          </button>
        </header>
        <div className="rpt-drawer__body">{children}</div>
        {footer && <footer className="rpt-drawer__foot">{footer}</footer>}
      </aside>
    </div>,
    document.body
  );
}

const shotUrl = (shot) => {
  const url = typeof shot === 'string' ? shot : shot?.url || shot?.screenshot_url || shot?.path || null;
  return typeof url === 'string' && /^(https?:|\/)/.test(url) ? url : null;
};

export function MetaGrid({ items }) {
  return (
    <dl className="rpt-meta">
      {items
        .filter((i) => i && i.value !== undefined)
        .map((i) => (
          <div className="rpt-meta__item" key={i.label}>
            <dt>{i.label}</dt>
            <dd>{i.value ?? '—'}</dd>
          </div>
        ))}
    </dl>
  );
}

export function RunStrip({ runs }) {
  const shown = runs.slice(0, 20).reverse();
  return (
    <div className="rpt-runstrip" role="img" aria-label={`Run history, oldest to newest: ${shown.map((r) => r.status).join(', ')}`}>
      {shown.map((run, i) => (
        <span
          key={`${run.executionId ?? i}-${run.time}`}
          className={`rpt-runstrip__dot rpt-runstrip__dot--${run.status.toLowerCase()}`}
          title={`${run.status} · ${formatDateTime(run.executedAt)}`}
        />
      ))}
    </div>
  );
}

export function TestCaseDetails({ row, history, historyLoading, historyTruncated }) {
  const status = normalizeStatus(row?.status);
  const screenshots = Array.isArray(row?.screenshots) ? row.screenshots : [];
  const shotUrls = screenshots.map(shotUrl).filter(Boolean).slice(0, 6);

  return (
    <div className="rpt-drawer-stack">
      <MetaGrid
        items={[
          { label: 'Status', value: <StatusPill status={status} /> },
          history && history.failureCount > 0
            ? { label: 'Pattern', value: <PatternBadge pattern={history.pattern} /> }
            : null,
          { label: 'Suite', value: row?.suite_name || '—' },
          { label: 'Test plan', value: row?.test_plan_name || 'Individual run' },
          { label: 'Environment', value: row?.environment_name || '—' },
          { label: 'Duration', value: formatDuration(row?.duration) },
          { label: 'Executed', value: formatDateTime(row?.executed_at) },
          { label: 'Screenshots', value: screenshots.length },
        ]}
      />

      {status === 'Failed' && (
        <div className="rpt-callout rpt-callout--info">
          <LuInfo size={15} aria-hidden />
          <p>
            Failure messages and stack traces aren't part of report data. Open the execution to see its
            logs and captured screenshots.
          </p>
        </div>
      )}

      {shotUrls.length > 0 && (
        <div>
          <h3 className="rpt-drawer-h">Screenshots</h3>
          <div className="rpt-shots">
            {shotUrls.map((url) => (
              <a key={url} href={url} target="_blank" rel="noreferrer" className="rpt-shots__item">
                <img src={url} alt="Execution screenshot" loading="lazy" />
              </a>
            ))}
          </div>
        </div>
      )}

      <div>
        <h3 className="rpt-drawer-h">History in this period</h3>
        {historyLoading && <ReportSkeleton variant="list" rows={3} />}
        {!historyLoading && !history && (
          <p className="rpt-empty-inline">No other runs of this test were found in this period.</p>
        )}
        {!historyLoading && history && (
          <>
            <p className="rpt-history-summary">
              <strong>
                Failed {history.failureCount} of {history.runCount} run{history.runCount !== 1 ? 's' : ''}
              </strong>
              {history.failStreak > 1 && <span> · last {history.failStreak} runs failed in a row</span>}
              {history.pattern !== 'stable' && (
                <span className="rpt-muted"> · {PATTERN_META[history.pattern]?.hint}</span>
              )}
            </p>
            <RunStrip runs={history.runs} />
            <ul className="rpt-runlist">
              {history.runs.slice(0, 8).map((run, i) => (
                <li key={`${run.executionId ?? i}-${run.time}`}>
                  <StatusPill status={run.status} />
                  <span className="rpt-runlist__date">{formatDateTime(run.executedAt)}</span>
                  <span className="rpt-runlist__env">{run.environment || '—'}</span>
                  <span className="rpt-runlist__dur">{formatDuration(run.duration)}</span>
                </li>
              ))}
            </ul>
            {historyTruncated && (
              <p className="rpt-caption">Based on the most recent executions loaded for this period.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function CaseMiniTable({ cases }) {
  if (!cases.length) return <p className="rpt-empty-inline">No test case results recorded.</p>;
  const ordered = [...cases].sort(
    (a, b) => (normalizeStatus(a.status) === 'Failed' ? -1 : 0) - (normalizeStatus(b.status) === 'Failed' ? -1 : 0)
  );
  return (
    <div className="rpt-table-wrap">
      <table className="rpt-table rpt-table--mini">
        <thead>
          <tr>
            <th scope="col">Test case</th>
            <th scope="col">Status</th>
            <th scope="col" className="is-right">Duration</th>
          </tr>
        </thead>
        <tbody>
          {ordered.map((tc, i) => (
            <tr key={tc.test_case_id ?? i} className={normalizeStatus(tc.status) === 'Failed' ? 'is-failed' : undefined}>
              <td>{tc.test_case_name || '—'}</td>
              <td><StatusPill status={tc.status} /></td>
              <td className="is-right rpt-mono">{formatDuration(tc.duration)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function SuiteRunDetails({ run }) {
  const cases = Array.isArray(run?.test_cases) ? run.test_cases : [];
  return (
    <div className="rpt-drawer-stack">
      <MetaGrid
        items={[
          { label: 'Status', value: <StatusPill status={run?.execution_status} /> },
          { label: 'Pass rate', value: <PassRateBar rate={run?.pass_rate} compact /> },
          { label: 'Environment', value: run?.environment_name || '—' },
          { label: 'Executed via', value: run?.executed_via || '—' },
          { label: 'Executed by', value: run?.executed_by || '—' },
          { label: 'Duration', value: formatDuration(run?.duration) },
          { label: 'Executed', value: formatDateTime(run?.executed_at) },
        ]}
      />
      <div className="rpt-result-chips">
        <span className="rpt-result-chip rpt-result-chip--success">Passed <b>{run?.passed ?? 0}</b></span>
        <span className="rpt-result-chip rpt-result-chip--danger">Failed <b>{run?.failed ?? 0}</b></span>
        <span className="rpt-result-chip rpt-result-chip--warning">Pending <b>{run?.pending ?? 0}</b></span>
        <span className="rpt-result-chip">Skipped <b>{run?.skipped ?? 0}</b></span>
      </div>
      <div>
        <h3 className="rpt-drawer-h">Test cases ({cases.length})</h3>
        <CaseMiniTable cases={cases} />
      </div>
    </div>
  );
}

export function PlanRunDetails({ run }) {
  const suites = Array.isArray(run?.suites) ? run.suites : [];
  return (
    <div className="rpt-drawer-stack">
      <MetaGrid
        items={[
          { label: 'Status', value: <StatusPill status={run?.execution_status} /> },
          { label: 'Pass rate', value: <PassRateBar rate={run?.pass_rate} compact /> },
          { label: 'Test cases', value: run?.total_test_cases ?? 0 },
          { label: 'Duration', value: formatDuration(run?.duration) },
          { label: 'Executed', value: formatDateTime(run?.executed_at) },
        ]}
      />
      <div>
        <h3 className="rpt-drawer-h">Suites ({suites.length})</h3>
        {!suites.length && <p className="rpt-empty-inline">This execution has no linked suite results.</p>}
        <div className="rpt-disclosure-list">
          {suites.map((suite, i) => {
            const failed = Number(suite.failed) > 0;
            return (
              <details
                key={suite.suite_execution_id || suite.suite_id || i}
                className="rpt-disclosure"
                open={failed && suites.length <= 4}
              >
                <summary>
                  <LuChevronRight size={14} className="rpt-disclosure__caret" aria-hidden />
                  <span className="rpt-disclosure__name">{suite.suite_name || '—'}</span>
                  <StatusPill status={suite.execution_status} />
                  <span className="rpt-disclosure__meta">
                    <span className="rpt-text--success">{suite.passed ?? 0} passed</span>
                    <span className={failed ? 'rpt-text--danger' : 'rpt-muted'}>{suite.failed ?? 0} failed</span>
                  </span>
                </summary>
                <div className="rpt-disclosure__body">
                  <CaseMiniTable cases={Array.isArray(suite.test_cases) ? suite.test_cases : []} />
                </div>
              </details>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function DrawerActions({ actions }) {
  return (
    <>
      {actions.filter(Boolean).map((a) => (
        <button
          key={a.label}
          type="button"
          className={`rt-btn rt-btn--${a.primary ? 'primary' : 'secondary'} rt-btn--sm`}
          onClick={a.onClick}
        >
          <span>{a.label}</span>
          <LuExternalLink size={13} aria-hidden />
        </button>
      ))}
    </>
  );
}
